import { useEffect, useRef, useState } from 'react'
import { Camera, RotateCcw } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'

/** A captured frame: the JPEG to send, and a URL to show it. */
export interface Shot {
  blob: Blob
  url: string
}

const TARGET_WIDTH = 720

/**
 * Takes one or more photos from the device camera.
 *
 * <p>The camera opens only while the dialog is open and is released the moment it closes —
 * a camera light that stays on after the photo is taken is how people learn to distrust a page.
 * The preview is mirrored because that is what people expect of a front camera; the photo sent
 * is not, so it matches what a phone app would send.
 */
export function CameraCapture({
  open, title, description, count = 1, prompts, confirmLabel, busy, error, onConfirm, onCancel,
}: {
  open: boolean
  title: string
  description?: string
  /** How many photos to take before the confirm button is offered. */
  count?: number
  /** One line per photo, e.g. "Look straight at the camera". */
  prompts?: string[]
  confirmLabel: string
  busy?: boolean
  error?: string | null
  onConfirm: (shots: Shot[]) => void
  onCancel: () => void
}) {
  const video = useRef<HTMLVideoElement>(null)
  const stream = useRef<MediaStream | null>(null)
  const [shots, setShots] = useState<Shot[]>([])
  const [cameraError, setCameraError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    // Mounted fresh for each use (the pages render it only while open), so there is no
    // previous shot or error to clear here.
    if (!navigator.mediaDevices?.getUserMedia) {
      queueMicrotask(() => setCameraError('This browser cannot use the camera. Use the mobile app or another browser.'))
      return
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false })
      .then((media) => {
        if (cancelled) { media.getTracks().forEach((track) => track.stop()); return }
        stream.current = media
        if (video.current) {
          video.current.srcObject = media
          void video.current.play()
        }
      })
      .catch((caught: unknown) => {
        const name = caught instanceof DOMException ? caught.name : ''
        setCameraError(name === 'NotAllowedError'
          ? 'Camera access is blocked. Allow the camera for this site in your browser settings and try again.'
          : name === 'NotFoundError'
            ? 'No camera was found on this device.'
            : 'The camera could not be started. Close other apps using it and try again.')
      })
    return () => {
      cancelled = true
      stream.current?.getTracks().forEach((track) => track.stop())
      stream.current = null
    }
  }, [open])

  // Object URLs hold memory until revoked.
  useEffect(() => () => { shots.forEach((shot) => URL.revokeObjectURL(shot.url)) }, [shots])

  function capture() {
    const source = video.current
    if (!source || !source.videoWidth) return
    const scale = Math.min(1, TARGET_WIDTH / source.videoWidth)
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(source.videoWidth * scale)
    canvas.height = Math.round(source.videoHeight * scale)
    canvas.getContext('2d')?.drawImage(source, 0, 0, canvas.width, canvas.height)
    canvas.toBlob((blob) => {
      if (blob) setShots((previous) => [...previous, { blob, url: URL.createObjectURL(blob) }])
    }, 'image/jpeg', 0.9)
  }

  const done = shots.length >= count
  const prompt = prompts?.[Math.min(shots.length, (prompts?.length ?? 1) - 1)]

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next) onCancel() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[92vh] w-[min(32rem,94vw)] -translate-x-1/2 -translate-y-1/2 space-y-4 overflow-y-auto overscroll-contain rounded-[var(--radius-card)] bg-[var(--color-card)] p-6 shadow-[var(--shadow-popover)]">
        <div>
          <Dialog.Title className="text-base font-semibold text-[var(--color-heading)]">{title}</Dialog.Title>
          {description && (
            <Dialog.Description className="mt-1 text-sm text-[var(--color-muted)]">{description}</Dialog.Description>
          )}
        </div>

        {cameraError
          ? <p className="text-sm text-[var(--color-danger)]">{cameraError}</p>
          : (
            <div className="space-y-3">
              <div className="relative overflow-hidden rounded-[var(--radius-card)] bg-black">
                <video ref={video} playsInline muted className="aspect-[4/3] w-full -scale-x-100 object-cover" />
                {/* A face-sized oval, so people frame themselves the way the model wants. */}
                <div aria-hidden="true"
                  className="pointer-events-none absolute inset-0 m-auto h-3/4 w-1/2 rounded-[50%] border-2 border-white/70" />
              </div>
              {!done && prompt && (
                <p className="text-center text-sm font-medium text-[var(--color-heading)]">
                  {count > 1 ? `Photo ${shots.length + 1} of ${count}: ` : ''}{prompt}
                </p>
              )}
              {shots.length > 0 && (
                <div className="flex gap-2">
                  {shots.map((shot, index) => (
                    <img key={shot.url} src={shot.url} alt={`Photo ${index + 1}`}
                      className="size-16 rounded object-cover" />
                  ))}
                </div>
              )}
            </div>
          )}

        {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
          {shots.length > 0 && (
            <Button type="button" variant="outline" disabled={busy} onClick={() => setShots([])}>
              <RotateCcw className="size-4" aria-hidden="true" />
              Retake
            </Button>
          )}
          {!done && !cameraError && (
            <Button type="button" onClick={capture}>
              <Camera className="size-4" aria-hidden="true" />
              Take photo
            </Button>
          )}
          {done && (
            <Button type="button" disabled={busy} onClick={() => onConfirm(shots)}>
              {busy ? 'Checking…' : confirmLabel}
            </Button>
          )}
        </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
